/**
 * Localized versions of the résumé content, used by the laurel.sh shell
 * when it runs inside SliceOS with a non-English system language.
 * The portfolio site itself always renders the canonical English content.
 *
 * Identity fields (name, email, links, cvFile) are shared untouched.
 */
import { content, type SiteContent } from "./content";
import type { OsLang } from "./os/i18n-core";

const FR: SiteContent = {
  ...content,
  tagline: "Ingénieur logiciel full-stack & manager",
  intro:
    "Dix ans à transformer des idées en systèmes robustes et évolutifs — du frontend au backend, de l'infrastructure à l'IA — avec un leadership de bâtisseur, les mains dans le code.",
  location: "À distance",
  specs: [
    { label: "Expérience", value: "10+ ans" },
    { label: "Discipline", value: "Full-stack + IA" },
    { label: "Actuel", value: "Manager ing. / Tech Lead" },
    { label: "Basé", value: "À distance" },
  ],
  about: [
    "Ingénieur logiciel full-stack et manager de terrain, avec plus de dix ans d'expérience dans la conception de solutions logicielles complexes, de sites web et d'applications. Vétéran des startups, j'interviens sur tout le spectre — du frontend au backend, jusqu'à l'infrastructure et l'intégration d'IA.",
    "Ma force : transformer des idées en systèmes robustes et évolutifs, avec un œil affûté pour le design et l'expérience utilisateur. Leader empathique à l'esprit entrepreneurial, je soude les équipes, stimule l'innovation et contribue à la vision d'ensemble de chaque projet.",
  ],
  experience: [
    {
      role: "Manager ingénierie / Tech Lead",
      company: "Zeel Networks",
      period: "Janv. 2018 — aujourd'hui",
      location: "À distance",
      highlights: [
        "Direction d'équipes front-end web et mobile, tout en restant les mains dans le code, dans un environnement 100 % à distance.",
        "Coordination inter-équipes, résolution des blocages, et garantie de livrer un produit de qualité, évolutif et maintenable, conforme aux exigences.",
        "Construction du site de Zeel, du CMS interne et de l'outillage CI/CD à partir de zéro, avec l'évolutivité et la maintenabilité comme principes fondateurs.",
      ],
    },
    {
      role: "Consultant indépendant en ingénierie logicielle",
      company: "Indépendant",
      period: "Depuis 2020",
      highlights: [
        "Conception et lancement de sites web et systèmes pour des startups.",
        "Mise en place de fondations techniques alignées sur la vision de chaque entreprise.",
        "Pilotage de refontes pour l'évolutivité et l'optimisation des performances.",
      ],
    },
    {
      role: "Ingénieur full-stack",
      company: "Commure",
      period: "2017 — 2018",
      highlights: [
        "Développement d'une application mobile React Native.",
        "Création from scratch d'une application web React avec mode hors ligne.",
      ],
    },
    {
      role: "Développeur cloud full-stack",
      company: "Enkidoo",
      period: "2016 — 2017",
      highlights: [
        "Développement de solutions métier cloud au sein de l'équipe de développement.",
        "Participation à des projets de R&D en machine learning.",
      ],
    },
    {
      role: "Stagiaire ingénieur logiciel",
      company: "Agence spatiale canadienne",
      period: "Janv. 2016 — avr. 2016",
      highlights: [
        "Développement d'une plateforme web de jonction.",
        "Rédaction et exécution de procédures de test.",
      ],
    },
  ],
  skills: [
    { label: "Frontend", items: ["React", "React Native", "TypeScript", "JavaScript", "UI / UX"] },
    { label: "Backend & infra", items: ["Node.js", "API REST", "CMS", "CI/CD", "Cloud"] },
    { label: "Plateforme & IA", items: ["Tauri (desktop)", "Intégration IA", "Machine learning"] },
    { label: "Leadership", items: ["Gestion d'équipe", "Leadership technique", "Mentorat", "Équipes à distance"] },
  ],
  projects: [
    {
      ...content.projects[0],
      role: "Créateur & unique ingénieur",
      blurb:
        "Une suite desktop multiplateforme construite de bout en bout : un shell « fake-OS » Tauri + React qui héberge une douzaine d'applications autonomes, chacune empaquetée indépendamment et installée à la demande depuis un registre Cloudflare. Le tout repose sur une API hôte versionnée avec récupération après crash et rollback automatique, un auto-updater signé et un stockage local-first par application. Résultat : un logiciel qui redevient compréhensible — un bureau discret et léger d'outils ciblés qui fonctionnent hors ligne et savent se faire oublier.",
    },
    {
      ...content.projects[1],
      blurb:
        "Un SaaS web à crédits pour construire des chaînes de prompts IA indépendantes du modèle, pilotables par webhooks. Définissez les entrées, composez vos séquences de prompts et exécutez vos workflows — pensé pour rendre l'intégration et l'automatisation de l'IA simples.",
    },
  ],
  education: [
    {
      credential: "Baccalauréat en génie logiciel",
      institution: "École de technologie supérieure (ÉTS)",
      period: "Montréal, Canada",
      detail:
        "Au-delà de la formation, j'élargis continuellement mes compétences techniques en autodidacte, en suivant les dernières tendances et technologies de l'industrie.",
    },
  ],
};

const ES: SiteContent = {
  ...content,
  tagline: "Ingeniero de software full-stack y mánager",
  intro:
    "Una década convirtiendo ideas en sistemas robustos y escalables — frontend, backend, infraestructura e IA — con un liderazgo práctico, de constructor.",
  location: "Remoto",
  specs: [
    { label: "Experiencia", value: "10+ años" },
    { label: "Disciplina", value: "Full-stack + IA" },
    { label: "Actual", value: "Mánager de ing. / Tech Lead" },
    { label: "Ubicación", value: "Remoto" },
  ],
  about: [
    "Ingeniero de software full-stack y mánager práctico con más de una década de experiencia creando soluciones de software complejas, sitios web y aplicaciones. Como veterano de startups, trabajo en todo el espectro: del frontend y backend a la infraestructura y la integración de IA.",
    "Mi fortaleza es convertir ideas en sistemas robustos y escalables, con buen ojo para el diseño y la experiencia de usuario. Como líder empático con espíritu emprendedor, fomento el trabajo en equipo, impulso la innovación y aporto a la visión global de cada proyecto.",
  ],
  experience: [
    {
      role: "Mánager de ingeniería / Tech Lead",
      company: "Zeel Networks",
      period: "Ene 2018 — presente",
      location: "Remoto",
      highlights: [
        "Lidero equipos de ingeniería front-end en web y móvil sin dejar de programar, en un entorno totalmente remoto.",
        "Coordino esfuerzos entre equipos, resuelvo bloqueos y garantizo que cumplamos los requisitos entregando un producto de calidad, escalable y mantenible.",
        "Construí el sitio web de Zeel, el CMS interno y las herramientas de CI/CD desde cero, con la escalabilidad y la mantenibilidad como principios básicos.",
      ],
    },
    {
      role: "Consultor independiente de ingeniería de software",
      company: "Autónomo",
      period: "Desde 2020",
      highlights: [
        "Construí y lancé sitios web y sistemas para startups.",
        "Establecí bases técnicas alineadas con la visión de cada negocio.",
        "Lideré iniciativas de refactorización para escalabilidad y rendimiento optimizado.",
      ],
    },
    {
      role: "Ingeniero full-stack",
      company: "Commure",
      period: "2017 — 2018",
      highlights: [
        "Desarrollé una aplicación móvil en React Native.",
        "Construí desde cero una aplicación web en React con capacidades offline.",
      ],
    },
    {
      role: "Desarrollador cloud full-stack",
      company: "Enkidoo",
      period: "2016 — 2017",
      highlights: [
        "Trabajé en el equipo de desarrollo creando soluciones de negocio en la nube.",
        "Participé en proyectos de I+D con aprendizaje automático.",
      ],
    },
    {
      role: "Ingeniero de software en prácticas",
      company: "Agencia Espacial Canadiense",
      period: "Ene 2016 — Abr 2016",
      highlights: [
        "Desarrollé una plataforma web de interconexión.",
        "Redacté y ejecuté procedimientos de prueba.",
      ],
    },
  ],
  skills: [
    { label: "Frontend", items: ["React", "React Native", "TypeScript", "JavaScript", "UI / UX"] },
    { label: "Backend e infra", items: ["Node.js", "APIs REST", "CMS", "CI/CD", "Cloud"] },
    { label: "Plataforma e IA", items: ["Tauri (escritorio)", "Integración de IA", "Aprendizaje automático"] },
    { label: "Liderazgo", items: ["Gestión de equipos", "Liderazgo técnico", "Mentoría", "Equipos remotos"] },
  ],
  projects: [
    {
      ...content.projects[0],
      role: "Creador y único ingeniero",
      blurb:
        'Una suite de escritorio multiplataforma construida de principio a fin: un shell "fake-OS" en Tauri + React que aloja una docena de apps autónomas, cada una empaquetada de forma independiente e instalada bajo demanda desde un registro en Cloudflare. Se apoya en una API host versionada con recuperación ante fallos y rollback automático, un auto-actualizador firmado y almacenamiento local-first por app. El resultado es software que vuelve a sentirse comprensible: un escritorio silencioso y ligero de herramientas enfocadas que funcionan sin conexión y no estorban.',
    },
    {
      ...content.projects[1],
      blurb:
        "Un SaaS web basado en créditos para construir cadenas de prompts de IA agnósticas al modelo, operables mediante webhooks. Configura las entradas, compón secuencias de prompts y ejecuta tus flujos de trabajo: diseñado para simplificar la integración y automatización de IA.",
    },
  ],
  education: [
    {
      credential: "Grado en Ingeniería de Software",
      institution: "École de technologie supérieure (ÉTS)",
      period: "Montreal, Canadá",
      detail:
        "He ampliado continuamente mis habilidades técnicas más allá de la educación formal mediante aprendizaje autodidacta, al día con las últimas tendencias y tecnologías de la industria.",
    },
  ],
};

const JA: SiteContent = {
  ...content,
  tagline: "フルスタック・ソフトウェアエンジニア＆マネージャー",
  intro:
    "アイデアをスケーラブルで堅牢なシステムへと形にして10年。フロントエンド、バックエンド、インフラ、AIまで、手を動かすビルダーとしてリーダーシップを発揮しています。",
  location: "リモート",
  specs: [
    { label: "経験", value: "10年以上" },
    { label: "専門", value: "フルスタック + AI" },
    { label: "現職", value: "エンジニアリングマネージャー / テックリード" },
    { label: "拠点", value: "リモート" },
  ],
  about: [
    "フルスタックのソフトウェアエンジニアであり、自ら手を動かすマネージャーとして、複雑なソフトウェアソリューション、ウェブサイト、アプリの開発に10年以上携わってきました。スタートアップのベテランとして、フロントエンドからバックエンド、インフラ、AI統合まで、あらゆる領域を担当します。",
    "強みは、アイデアをスケーラブルで堅牢なシステムに変える力と、デザインやユーザー体験への鋭い感覚です。起業家精神を持つ共感型のリーダーとして、チームワークを育み、イノベーションを促し、関わる全てのプロジェクトの大きなビジョンに貢献します。",
  ],
  experience: [
    {
      role: "エンジニアリングマネージャー / テックリード",
      company: "Zeel Networks",
      period: "2018年1月 — 現在",
      location: "リモート",
      highlights: [
        "フルリモート環境で、ウェブとモバイルのフロントエンドチームを率いながら、自らもコードを書き続けています。",
        "チーム横断の取り組みを調整し、課題を解決し、要件を満たすスケーラブルで保守性の高い高品質なプロダクトの出荷を担保しています。",
        "Zeelのウェブサイト、社内CMS、CI/CDツールをゼロから構築。スケーラビリティと保守性を第一原則としました。",
      ],
    },
    {
      role: "独立系ソフトウェアエンジニアリングコンサルタント",
      company: "自営業",
      period: "2020年〜",
      highlights: [
        "スタートアップ向けのウェブサイトやシステムを構築・ローンチ。",
        "各ビジネスのビジョンに沿った技術基盤を整備。",
        "スケーラビリティ向上とパフォーマンス最適化のためのリファクタリングを主導。",
      ],
    },
    {
      role: "フルスタックエンジニア",
      company: "Commure",
      period: "2017 — 2018",
      highlights: [
        "React Nativeモバイルアプリを開発。",
        "オフライン対応のReactウェブアプリをゼロから構築。",
      ],
    },
    {
      role: "フルスタック・クラウド開発者",
      company: "Enkidoo",
      period: "2016 — 2017",
      highlights: [
        "開発チームの一員として、クラウドベースの業務ソリューションを構築。",
        "機械学習を扱う研究開発プロジェクトに参加。",
      ],
    },
    {
      role: "ソフトウェアエンジニア・インターン",
      company: "カナダ宇宙庁",
      period: "2016年1月 — 4月",
      highlights: ["ウェブ接続プラットフォームを開発。", "テスト手順書の作成と実施。"],
    },
  ],
  skills: [
    { label: "フロントエンド", items: ["React", "React Native", "TypeScript", "JavaScript", "UI / UX"] },
    { label: "バックエンド＆インフラ", items: ["Node.js", "REST API", "CMS", "CI/CD", "クラウド"] },
    { label: "プラットフォーム＆AI", items: ["Tauri（デスクトップ）", "AI統合", "機械学習"] },
    { label: "リーダーシップ", items: ["チームマネジメント", "技術リーダーシップ", "メンタリング", "リモートチーム"] },
  ],
  projects: [
    {
      ...content.projects[0],
      role: "開発者（単独開発）",
      blurb:
        "エンドツーエンドで構築したクロスプラットフォームのデスクトップスイート。Tauri + Reactによる「フェイクOS」シェルが、それぞれ独立にバンドルされCloudflareレジストリからオンデマンドでインストールされる十数個の自己完結型アプリをホストします。クラッシュ復旧と自動ロールバックを備えたバージョン管理されたホストAPI、署名付き自動アップデーター、アプリごとのローカルファーストストレージが土台。ソフトウェアが再び「理解できるもの」に感じられる — オフラインで動き、邪魔をしない、静かで軽量な道具のデスクトップです。",
    },
    {
      ...content.projects[1],
      blurb:
        "モデル非依存のAIプロンプトチェーンを構築できる、クレジット制のウェブSaaS。Webhookから操作可能。ワークフローの入力を設定し、プロンプトの連なりを組み立てて実行 — AI統合と自動化をシンプルにするための設計です。",
    },
  ],
  education: [
    {
      credential: "ソフトウェア工学 学士",
      institution: "École de technologie supérieure (ÉTS)",
      period: "モントリオール、カナダ",
      detail:
        "正規教育にとどまらず、自律的な学習で技術力を継続的に拡張し、業界の最新トレンドと技術を追い続けています。",
    },
  ],
};

export const contentFor = (lang: OsLang): SiteContent =>
  lang === "fr" ? FR : lang === "es" ? ES : lang === "ja" ? JA : content;
